/**
 * Agent Registry
 * Central registry of all available agents with their execution metadata
 */

import { AgentExecutionConfig } from './types';

export interface AgentDefinition {
  id: string;
  name: string;
  type: string;
  description: string;
  category: 'sdlc' | 'testing' | 'deployment' | 'analysis' | 'custom';
  icon?: string;
  
  // Execution configuration template
  executionConfig: AgentExecutionConfig;
}

// Agent Registry - All available agents
export const AGENT_REGISTRY: Record<string, AgentDefinition> = {
  'epic-stories-generator': {
    id: 'epic-stories-generator',
    name: 'Epic & Stories Generator',
    type: 'epic-stories-generator',
    description: 'Generate epics and user stories from BRD documents',
    category: 'sdlc',
    icon: '📋',
    executionConfig: {
      agentId: 'epic-stories-generator',
      agentName: 'Epic & Stories Generator',
      agentType: 'epic-stories-generator',
      scriptPath: './agents/generate-epic-and-user-stories.sh',
      workingDirectory: '.',
      inputs: {
        brdPath: {
          name: 'brdPath',
          type: 'file',
          required: true,
          description: 'Path to the Business Requirements Document',
          source: { type: 'userInput' }
        },
        existingAppBrd: {
          name: 'existingAppBrd',
          type: 'file',
          required: false,
          description: 'Path to existing application BRD',
          source: { type: 'userInput' }
        },
        existingAppArch: {
          name: 'existingAppArch',
          type: 'file',
          required: false,
          description: 'Path to existing application architecture',
          source: { type: 'userInput' }
        },
        workspaceRoot: {
          name: 'workspaceRoot',
          type: 'string',
          required: true,
          description: 'Workspace root directory',
          defaultValue: '.',
          source: { type: 'constant', value: '.' }
        },
        contextDirs: {
          name: 'contextDirs',
          type: 'string',
          required: false,
          description: 'Context directories (comma-separated)',
          defaultValue: 'src,docs',
          source: { type: 'constant', value: 'src,docs' }
        }
      },
      outputs: {
        epicJson: {
          name: 'epicJson',
          type: 'file',
          description: 'Generated epic in JSON format',
          filePath: 'sdlc-artifacts/epic-stories/epic.json'
        },
        storiesJson: {
          name: 'storiesJson',
          type: 'file',
          description: 'Generated user stories in JSON format',
          filePath: 'sdlc-artifacts/epic-stories/stories.json'
        },
        summary: {
          name: 'summary',
          type: 'file',
          description: 'Summary of generated epic and stories',
          filePath: 'sdlc-artifacts/epic-stories/summary.md'
        },
        stories: {
          name: 'stories',
          type: 'array',
          description: 'Array of story objects for iteration',
          jsonPath: '$.stories'
        }
      },
      timeout: 600,
      retryPolicy: {
        maxRetries: 2,
        retryDelay: 30,
        retryOn: ['TIMEOUT', 'NETWORK_ERROR']
      },
      policyFile: './agents/policies/epic-stories-generation.policy.md'
    }
  },

  'code-generator': {
    id: 'code-generator',
    name: 'Implementation Generator',
    type: 'code-generator',
    description: 'Generate code implementation for user stories',
    category: 'sdlc',
    icon: '💻',
    executionConfig: {
      agentId: 'code-generator',
      agentName: 'Implementation Generator',
      agentType: 'code-generator',
      scriptPath: './agents/generate-user-story-implementation.sh',
      workingDirectory: '.',
      inputs: {
        jiraTicketId: {
          name: 'jiraTicketId',
          type: 'string',
          required: true,
          description: 'JIRA ticket ID for the user story',
          source: { type: 'previousNode' }
        },
        epicId: {
          name: 'epicId',
          type: 'string',
          required: true,
          description: 'JIRA epic ID',
          source: { type: 'previousNode' }
        },
        workspaceRoot: {
          name: 'workspaceRoot',
          type: 'string',
          required: true,
          description: 'Workspace root directory',
          defaultValue: '.',
          source: { type: 'constant', value: '.' }
        },
        existingAppBrd: {
          name: 'existingAppBrd',
          type: 'file',
          required: false,
          description: 'Path to existing application BRD',
          source: { type: 'workflowVariable', variableName: 'existingAppBrd' }
        },
        existingAppArch: {
          name: 'existingAppArch',
          type: 'file',
          required: false,
          description: 'Path to existing application architecture',
          source: { type: 'workflowVariable', variableName: 'existingAppArch' }
        }
      },
      outputs: {
        branchName: {
          name: 'branchName',
          type: 'string',
          description: 'Created feature branch name',
          jsonPath: '$.branchName'
        },
        prNumber: {
          name: 'prNumber',
          type: 'number',
          description: 'Pull request number',
          jsonPath: '$.prNumber'
        },
        changesSummary: {
          name: 'changesSummary',
          type: 'file',
          description: 'Summary of code changes',
          filePath: 'sdlc-artifacts/implementation/changes-summary.md'
        }
      },
      timeout: 900,
      retryPolicy: {
        maxRetries: 1,
        retryDelay: 60,
        retryOn: ['TIMEOUT']
      },
      policyFile: './agents/policies/implementation.policy.md'
    }
  },

  'test-generator': {
    id: 'test-generator',
    name: 'Unit Test Generator',
    type: 'test-generator',
    description: 'Generate unit tests for implemented code',
    category: 'testing',
    icon: '🧪',
    executionConfig: {
      agentId: 'test-generator',
      agentName: 'Unit Test Generator',
      agentType: 'test-generator',
      scriptPath: './agents/generate-user-story-unit-tests.sh',
      workingDirectory: '.',
      inputs: {
        jiraTicketId: {
          name: 'jiraTicketId',
          type: 'string',
          required: true,
          description: 'JIRA ticket ID',
          source: { type: 'previousNode' }
        },
        epicId: {
          name: 'epicId',
          type: 'string',
          required: true,
          description: 'JIRA epic ID',
          source: { type: 'previousNode' }
        },
        storyBranch: {
          name: 'storyBranch',
          type: 'string',
          required: true,
          description: 'Feature branch name',
          source: { type: 'previousNode', outputKey: 'branchName' }
        }
      },
      outputs: {
        testPlan: {
          name: 'testPlan',
          type: 'file',
          description: 'Unit test plan',
          filePath: 'sdlc-artifacts/unit-tests/unit-test-plan.md'
        },
        testsGenerated: {
          name: 'testsGenerated',
          type: 'boolean',
          description: 'Whether tests were generated successfully'
        }
      },
      timeout: 600,
      policyFile: './agents/policies/unit-tests.policy.md'
    }
  },

  'test-executor': {
    id: 'test-executor',
    name: 'Test Executor',
    type: 'test-executor',
    description: 'Execute unit tests and report results',
    category: 'testing',
    icon: '▶️',
    executionConfig: {
      agentId: 'test-executor',
      agentName: 'Test Executor',
      agentType: 'test-executor',
      scriptPath: './agents/run-tests-with-retry.sh',
      workingDirectory: '.',
      inputs: {
        testCommand: {
          name: 'testCommand',
          type: 'string',
          required: false,
          description: 'Test command to execute',
          defaultValue: 'npm test',
          source: { type: 'constant', value: 'npm test' }
        },
        maxRetries: {
          name: 'maxRetries',
          type: 'number',
          required: false,
          description: 'Maximum retry attempts',
          defaultValue: 5,
          source: { type: 'constant', value: 5 }
        }
      },
      outputs: {
        testResults: {
          name: 'testResults',
          type: 'object',
          description: 'Test execution results',
          jsonPath: '$.results'
        },
        passPercentage: {
          name: 'passPercentage',
          type: 'number',
          description: 'Percentage of tests passed',
          jsonPath: '$.passPercentage'
        },
        allTestsPassed: {
          name: 'allTestsPassed',
          type: 'boolean',
          description: 'Whether all tests passed',
          jsonPath: '$.allPassed'
        }
      },
      timeout: 1800
    }
  },

  'pr-creator': {
    id: 'pr-creator',
    name: 'Pull Request Creator',
    type: 'pr-creator',
    description: 'Create pull request for code changes',
    category: 'sdlc',
    icon: '🔀',
    executionConfig: {
      agentId: 'pr-creator',
      agentName: 'Pull Request Creator',
      agentType: 'pr-creator',
      scriptPath: './agents/create-pr.sh',
      workingDirectory: '.',
      inputs: {
        branchName: {
          name: 'branchName',
          type: 'string',
          required: true,
          description: 'Feature branch name',
          source: { type: 'previousNode', outputKey: 'branchName' }
        },
        title: {
          name: 'title',
          type: 'string',
          required: true,
          description: 'PR title',
          source: { type: 'previousNode', outputKey: 'prTitle' }
        },
        description: {
          name: 'description',
          type: 'string',
          required: false,
          description: 'PR description',
          source: { type: 'previousNode', outputKey: 'prDescription' }
        }
      },
      outputs: {
        prNumber: {
          name: 'prNumber',
          type: 'number',
          description: 'Created PR number'
        },
        prUrl: {
          name: 'prUrl',
          type: 'string',
          description: 'PR URL'
        }
      },
      timeout: 300
    }
  }
};

// Helper function to get agent by type
export function getAgentByType(agentType: string): AgentDefinition | undefined {
  return AGENT_REGISTRY[agentType];
}

// Helper function to get all agents by category
export function getAgentsByCategory(category: string): AgentDefinition[] {
  return Object.values(AGENT_REGISTRY).filter(agent => agent.category === category);
}

// Helper function to list all agents
export function getAllAgents(): AgentDefinition[] {
  return Object.values(AGENT_REGISTRY);
}

